import get from 'lodash.get';
import { blocks, site, view } from '@yola/ws-sdk';
import dialogTypes from 'src/js/modules/dialogs/constants/dialog-types';
import segment from 'src/js/modules/analytics/segment';
import getPageSettingsTraits from 'src/js/modules/analytics/segment/helpers/get-page-settings-traits';
import getParentBlockByElement from 'src/js/modules/blocks/helpers/get-parent-block-by-element';
import adjacentPositions from 'src/js/modules/blocks/constants/adjacent-positions';
import websiteDesignTriggerIds from 'src/js/modules/website-design/constants/trigger-ids';

const {
  track,
  trackers: { trackEvent },
  constants: { events, triggerIds },
  helpers: { getDefaultTraits },
} = segment;

const { AFTER_BEGIN, BEFORE_END } = adjacentPositions;

function onDialogDisplayed({ type, modalProps = {} }) {
  switch (type) {
    case dialogTypes.BLOCK_LAYOUT_DIALOG:
    case dialogTypes.BLOCK_SETTINGS:
    case dialogTypes.SELECT_ICON:
    case dialogTypes.CONTACT_FORM_EDIT_FIELD_DIALOG:
    case dialogTypes.VOTE_FOR_THE_FEATURE:
    case dialogTypes.ELEMENT_SETTINGS_DIALOG:
    case dialogTypes.PUBLISH_INTERRUPTION_DIALOG:
    case dialogTypes.WEBSITE_SETTINGS:
    case dialogTypes.DIFM_ORDER_FORM_DIALOG:
    case dialogTypes.FREE_TRIAL_PUBLISHING_FLOW_DIALOG:
    case dialogTypes.CODE_SNIPPET_DIALOG:
    case dialogTypes.TEXT_CUSTOM_SPACING_DIALOG:
    case dialogTypes.ADVANCED_COLOR_PICKER_DIALOG:
    case dialogTypes.ADD_COLOR_SCHEME_DIALOG:
    case dialogTypes.TRANSLATION_PROGRESS_DIALOG: {
      break;
    }

    case dialogTypes.LINK_FORMATTING_DIALOG:
    case dialogTypes.EDIT_LINK_DIALOG:
    case dialogTypes.NAVIGATION_ITEM_SETTINGS_DIALOG:
    case dialogTypes.FOOTER_NAVIGATION_ITEM_SETTING_DIALOG: {
      trackEvent(events.LINK_SETTINGS_DIALOG_DISPLAYED, {
        dialogId: type,
      });
      break;
    }
    case dialogTypes.FOOTER_NAVIGATION_SETTINGS_DIALOG: {
      const element = view.accessors.getLiveElement(modalProps.elementId);
      const blockElement = getParentBlockByElement(element);

      trackEvent(events.LIST_SETTINGS_DIALOG_DISPLAYED, {
        dialogId: type,
        blockId: blocks.accessors.getBlockIdByElement(blockElement),
        blockVariationId: blocks.accessors.getVariationIdByElement(blockElement),
      });
      break;
    }
    case dialogTypes.SOCIAL_LINKS_SETTINGS_DIALOG: {
      const element = view.accessors.getLiveElement(modalProps.elementId);
      const blockElement = getParentBlockByElement(element);

      trackEvent(events.SOCIAL_LINKS_SETTINGS_DIALOG_DISPLAYED, {
        dialogId: type,
        blockId: blocks.accessors.getBlockIdByElement(blockElement),
        blockVariationId: blocks.accessors.getVariationIdByElement(blockElement),
      });
      break;
    }
    case dialogTypes.SOCIAL_LINKS_ITEM_SETTINGS_DIALOG: {
      const element = view.accessors.getLiveElement(modalProps.elementId);
      const blockElement = getParentBlockByElement(element);

      if (modalProps.targetLink) {
        trackEvent(events.SOCIAL_LINKS_EDIT_LINK_DIALOG_DISPLAYED, {
          dialogId: type,
          blockId: blocks.accessors.getBlockIdByElement(blockElement),
          blockVariationId: blocks.accessors.getVariationIdByElement(blockElement),
        });
      } else {
        trackEvent(events.SOCIAL_LINKS_ADD_LINK_DIALOG_DISPLAYED, {
          dialogId: type,
          blockId: blocks.accessors.getBlockIdByElement(blockElement),
          blockVariationId: blocks.accessors.getVariationIdByElement(blockElement),
        });
      }
      break;
    }
    case dialogTypes.ADD_WIDGET: {
      const { adjacentPosition, targetElementId } = modalProps;
      const element =
        adjacentPosition === AFTER_BEGIN || adjacentPosition === BEFORE_END
          ? view.accessors.getLiveElement(targetElementId)
          : view.accessors.getParentLiveElement(targetElementId);
      const blockElement = getParentBlockByElement(element);

      trackEvent(events.WIDGET_SELECTOR_DISPLAYED, {
        targetBlockId: blocks.accessors.getBlockIdByElement(blockElement),
        targetBlockVariationId: blocks.accessors.getVariationIdByElement(blockElement),
      });

      break;
    }

    case dialogTypes.TRANSLATE_PAGE: {
      const { bulkEditing } = modalProps;
      trackEvent(events.TRANSLATE_PAGE_DIALOG_DISPLAYED, { bulkEditing });
      break;
    }

    case dialogTypes.TRANSLATION_COMPLETE_DIALOG: {
      const { totalTranslatedPages, totalFailedPages } = modalProps;
      trackEvent(events.TRANSLATE_PAGE_COMPLETED_DIALOG_DISPLAYED, {
        totalTranslatedPages,
        totalFailedPages,
      });
      break;
    }

    case dialogTypes.MULTILINGUAL_INTRO: {
      const { triggerId } = modalProps;

      trackEvent(events.MULTILINGUAL_INTRO_DISPLAYED, {
        triggerId,
      });
      break;
    }

    case dialogTypes.REDIRECT_DELETE_DIALOG: {
      trackEvent(events.DELETE_REDIRECT_DIALOG_DISPLAYED);

      break;
    }
    case dialogTypes.READ_ONLY_ALERT_DIALOG: {
      trackEvent(events.READONLY_INTERRUPTION_DISPLAYED);

      break;
    }
    case dialogTypes.REDIRECT_EDIT_DIALOG: {
      if (modalProps.redirect) {
        trackEvent(events.EDIT_REDIRECT_DIALOG_DISPLAYED);
      } else {
        trackEvent(events.ADD_REDIRECT_DIALOG_DISPLAYED);
      }

      break;
    }
    case dialogTypes.MEDIA_SETTINGS_DIALOG: {
      const element = view.accessors.getLiveElement(modalProps.elementId);
      const blockElement = getParentBlockByElement(element);

      trackEvent(events.MEDIA_SETTINGS_DIALOG_DISPLAYED, {
        dialogId: type,
        blockId: blocks.accessors.getBlockIdByElement(blockElement),
        blockVariationId: blocks.accessors.getVariationIdByElement(blockElement),
      });

      break;
    }
    case dialogTypes.CONTACT_FORM_SETTINGS_DIALOG: {
      const element = view.accessors.getLiveElement(modalProps.elementId);
      const blockElement = getParentBlockByElement(element);

      trackEvent(events.CONTACT_FORM_SETTINGS_DIALOG_DISPLAYED, {
        dialogId: type,
        blockId: blocks.accessors.getBlockIdByElement(blockElement),
        blockVariationId: blocks.accessors.getVariationIdByElement(blockElement),
      });

      break;
    }
    case dialogTypes.CONTACT_FORM_SELECT_ITEM_DIALOG: {
      track(events.CONTACT_FORM_SELECT_ITEM_DIALOG_DISPLAYED, {
        dialogId: type,
        ...getDefaultTraits(modalProps.elementId),
      });

      break;
    }
    case dialogTypes.CONTACT_FORM_DELETE_EMAIL_DIALOG: {
      track(events.CONTACT_FORM_DELETE_EMAIL_DIALOG_DISPLAYED, {
        dialogId: type,
        ...getDefaultTraits(modalProps.elementId),
      });

      break;
    }
    case dialogTypes.GALLERY_DIALOG: {
      const element = view.accessors.getLiveElement(modalProps.elementId);
      const blockElement = getParentBlockByElement(element);
      trackEvent(events.GALLERY_SETTINGS_DISPLAYED, {
        dialogId: type,
        blockId: blocks.accessors.getBlockIdByElement(blockElement),
      });

      break;
    }
    case dialogTypes.SLIDER_SETTINGS_DIALOG: {
      const element = view.accessors.getLiveElement(modalProps.elementId);
      const blockElement = getParentBlockByElement(element);
      trackEvent(events.SLIDER_SETTINGS_DISPLAYED, {
        dialogId: type,
        blockId: blocks.accessors.getBlockIdByElement(blockElement),
      });

      break;
    }
    case dialogTypes.DIFM_PROMO_BANNER_DIALOG:
    case dialogTypes.DIFM_PROMO_SERVICES_DIALOG:
    case dialogTypes.DIFM_PROMO_SURVEY_DIALOG:
    case dialogTypes.DIFM_PROMO_PACKAGES_DIALOG: {
      const { triggerId, values } = modalProps;
      const serviceName = get(values, 'serviceName', null);

      trackEvent(events.DIFM_PROMO_DIALOG_DISPLAYED, {
        dialogId: type,
        triggerId,
        selectedService: serviceName,
      });

      break;
    }
    case dialogTypes.DIFM_PROMO_FINISH_DIALOG: {
      const { selectedService, selectedPackage } = modalProps;
      trackEvent(events.DIFM_PROMO_DIALOG_FINISH_DISPLAYED, {
        dialogId: type,
        selectedService,
        selectedPackage,
      });

      break;
    }

    case dialogTypes.BLOCK_REPLACEMENT_DIALOG: {
      const { oldBlock, oldBlockVariationId, newBlock, newBlockVariationId } = modalProps;

      trackEvent(events.BLOCK_REPLACEMENT_DIALOG_DISPLAYED, {
        dialogId: type,
        oldBlock,
        oldBlockVariationId,
        newBlock,
        newBlockVariationId,
      });

      break;
    }

    case dialogTypes.BLOCK_LIMIT_DIALOG: {
      const { blockId } = modalProps;

      trackEvent(events.BLOCK_LIMIT_DIALOG_DISPLAYED, {
        dialogId: type,
        blockId,
      });

      break;
    }

    case dialogTypes.DESIGN_SETTINGS_CUSTOM_PALETTE: {
      const { triggerId, colorPreset } = modalProps;

      trackEvent(events.DESIGN_SETTINGS_COLOR_PRESET_SETTINGS_DISPLAYED, {
        dialogId: type,
        triggerId,
        colorPreset,
      });
      break;
    }

    case dialogTypes.ADVANCED_COLORS_SETTINGS_DIALOG: {
      const { colorSchemeId, triggerId } = modalProps;

      if (!triggerId) {
        break;
      }

      trackEvent(events.DESIGN_SETTINGS_COLOR_SCHEME_SETTINGS_DISPLAYED, {
        colorScheme: colorSchemeId,
        triggerId,
      });
      break;
    }

    case dialogTypes.BASE_COLORS_PICKER_DIALOG: {
      const { colorPreset, triggerId } = modalProps;
      const isNewPaletteTrigger = triggerId === websiteDesignTriggerIds.ADD_CUSTOM_PALETTE;

      if (isNewPaletteTrigger) {
        trackEvent(events.DESIGN_SETTINGS_NEW_PALETTE_DISPLAYED);
        break;
      } else {
        trackEvent(events.DESIGN_SETTINGS_EDIT_PALETTE_DISPLAYED, {
          colorPreset,
        });
        break;
      }
    }

    case dialogTypes.DESIGN_SETTINGS_EFFECTS_DIALOG: {
      const { sourceId } = modalProps;

      trackEvent(events.DESIGN_SETTINGS_EFFECTS_DIALOG_DISPLAYED, {
        sourceId,
      });
      break;
    }

    case dialogTypes.DESIGN_SETTINGS_BUTTONS_DIALOG: {
      const { sourceId } = modalProps;

      trackEvent(events.DESIGN_SETTINGS_BUTTONS_DIALOG_DISPLAYED, {
        sourceId,
      });
      break;
    }

    case dialogTypes.DESIGN_SETTINGS_FONTS_DIALOG: {
      const { sourceId } = modalProps;

      trackEvent(events.DESIGN_SETTINGS_FONTS_DIALOG_DISPLAYED, {
        sourceId,
      });
      break;
    }

    case dialogTypes.DESIGN_SETTINGS_COLORS_DIALOG: {
      const { sourceId } = modalProps;

      trackEvent(events.DESIGN_SETTINGS_COLORS_DIALOG_DISPLAYED, {
        sourceId,
      });
      break;
    }

    case dialogTypes.BUTTON_PRESET_SETTINGS: {
      const { presetId: buttonPreset } = modalProps;

      trackEvent(events.DESIGN_SETTINGS_BUTTON_PRESET_SETTINGS_DISPLAYED, {
        buttonPreset,
      });
      break;
    }

    case dialogTypes.DESIGN_SETTINGS_OTHER_STYLES_DIALOG: {
      const { sourceId } = modalProps;
      trackEvent(events.DESIGN_SETTINGS_OTHER_STYLES_DIALOG_DISPLAYED, {
        sourceId,
      });
      break;
    }

    case dialogTypes.ADD_FONT_PAIR_DIALOG: {
      trackEvent(events.DESIGN_SETTINGS_ADD_NEW_FONT_PAIR_DISPLAYED, {
        dialogId: type,
      });
      break;
    }

    case dialogTypes.DESIGN_SETTINGS_FONT_SETTINGS_DIALOG: {
      const { fontPairId } = modalProps;

      trackEvent(events.DESIGN_SETTINGS_FONT_SETTINGS_DISPLAYED, {
        dialogId: type,
        currentFontPairId: fontPairId,
      });
      break;
    }

    case dialogTypes.SITE_WIZARD_SETUP_DIALOG:
    case dialogTypes.AI_TEXT_ASSISTANT_ERROR_DIALOG: {
      break;
    }

    case dialogTypes.SITE_WIZARD_CONFIRM_EXIT_DIALOG: {
      trackEvent(events.AI_WIZARD_CLOSE_DIALOG_DISPLAYED);
      break;
    }

    case dialogTypes.SITE_WIZARD_PROGRESS_DIALOG:
    case dialogTypes.PAGE_GENERATION_DIALOG: {
      trackEvent(events.AI_WIZARD_GENERATING_DIALOG_DISPLAYED, {
        businessCategory: site.accessors.getBusinessCategory(),
      });
      break;
    }

    case dialogTypes.AI_ERROR_DIALOG: {
      trackEvent(events.AI_WIZARD_ERROR_DIALOG_DISPLAYED, {
        businessCategory: site.accessors.getBusinessCategory(),
      });
      break;
    }

    case dialogTypes.AI_ONBOARDING_DIALOG: {
      const { sourceId } = modalProps;

      trackEvent(events.AI_WIZARD_ONBOARDING_DIALOG_DISPLAYED, {
        sourceId,
        businessCategory: site.accessors.getBusinessCategory(),
      });
      break;
    }

    case dialogTypes.ADD_PAGE_DIALOG:
    case dialogTypes.NEW_PAGE_DIALOG: {
      trackEvent(events.ADD_PAGE_DIALOG_DISPLAYED, {
        triggerId: modalProps.triggerId || triggerIds.ADD_PAGE,
      });
      break;
    }

    case dialogTypes.CREATE_FOLDER_DIALOG: {
      trackEvent(events.ADD_FOLDER_DIALOG_DISPLAYED, {
        triggerId: modalProps.triggerId,
      });
      break;
    }

    case dialogTypes.DELETE_PAGE:
    case dialogTypes.DELETE_FOLDER:
    case dialogTypes.BULK_EDITING_DELETE_DIALOG: {
      const { itemType, bulkEditing, itemsCount } = modalProps;
      trackEvent(events.PAGE_MANAGER_DELETE_DIALOG_DISPLAYED, {
        itemType,
        bulkEditing,
        itemsCount,
      });
      break;
    }

    case dialogTypes.BULK_EDITING_MOVE_TO_DIALOG: {
      const { bulkEditing, itemsCount } = modalProps;
      trackEvent(events.PAGE_MANAGER_MOVE_TO_DIALOG_DISPLAYED, {
        bulkEditing,
        itemsCount,
      });
      break;
    }

    case dialogTypes.FOLDER_SETTINGS: {
      trackEvent(events.FOLDER_SETTINGS_DIALOG_DISPLAYED, {
        folderName: modalProps.folderName,
      });
      break;
    }

    case dialogTypes.PAGE_SETTINGS: {
      const pageSettings = site.accessors.getPage(modalProps.pageId);

      trackEvent(events.PAGE_SETTINGS_DISPLAYED, {
        dialogId: type,
        ...getPageSettingsTraits(pageSettings, {
          displayInNavigation: site.verifiers.isPageInNavigationGlobal(modalProps.pageId),
        }),
      });
      break;
    }

    case dialogTypes.TEXT_DECORATION_SETTINGS_DIALOG: {
      const { premiumAvailable } = modalProps;

      trackEvent(events.TEXT_DECORATION_SETTINGS_DIALOG_DISPLAYED, {
        premiumAvailable,
      });

      break;
    }

    case dialogTypes.TEXT_DECORATION_LIBRARY_DIALOG: {
      const { premiumAvailable } = modalProps;

      trackEvent(events.TEXT_DECORATION_PRESETS_DIALOG_DISPLAYED, {
        premiumAvailable,
      });

      break;
    }

    case dialogTypes.DIFM_UPSELL_DIALOG: {
      const businessCategory = site.accessors.getBusinessCategory();

      trackEvent(events.DIFM_UPSELL_DISPLAYED, {
        businessCategory: businessCategory || null,
      });

      break;
    }

    case dialogTypes.EMBED_HTML_SETTINGS_DIALOG: {
      const { renderInEditor } = modalProps;

      trackEvent(events.CUSTOM_HTML_DIALOG_DISPLAYED, {
        renderInEditor,
      });

      break;
    }

    case dialogTypes.PAGE_MANAGER: {
      const { triggerId } = modalProps;

      if (triggerId) {
        trackEvent(events.PAGE_MANAGER_DIALOG_DISPLAYED, {
          triggerId,
        });
      }
      break;
    }

    case dialogTypes.ADD_LANGUAGE: {
      const { triggerId } = modalProps;

      trackEvent(events.TRANSLATE_WEBSITE_DIALOG_DISPLAYED, {
        triggerId,
      });
      break;
    }

    default: {
      trackEvent(events.DIALOG_DISPLAYED, {
        dialogId: type,
      });
    }
  }
}

export default onDialogDisplayed;
